import { useEffect, useMemo, useRef, useState } from "react";
import { Heart } from 'lucide-react';
import ProductGallery from "./ProductGallery";
import ColorSelector from "./ColorSelector";
import SizeSelector from "./SizeSelector";
import ProductDetails from "./ProductDetails";
import RecommendedProducts from "./RecommendedProducts";
import Footer from "../../Footer";
import NavBar from "../../NavBar";
import SectionBreak from "../../SectionBreak";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchProducts, findProductById, getFallbackProducts } from "../productService";
import { useShop } from '../../../context/ShopContext';
import { toast } from 'react-hot-toast';

const Index = () => {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fallbackProducts = useMemo(() => getFallbackProducts(), []);
  const { addToCart, toggleWishlist, isInWishlist } = useShop();
  const sizeSelectorRef = useRef(null);

  const productsFromState = useMemo(() => {
    const list = location.state?.productsList;
    return Array.isArray(list) && list.length > 0 ? list : null;
  }, [location.state]);

  const initialProductFromState = useMemo(() => {
    const candidate = location.state?.product;
    if (!candidate) return null;
    const candidateId = candidate.id || candidate.productCode;
    if (!candidateId || !productId) {
      return null;
    }
    return candidateId.toString().toLowerCase() === productId.toLowerCase()
      ? candidate
      : null;
  }, [location.state, productId]);

  const [product, setProduct] = useState(initialProductFromState);
  const [allProducts, setAllProducts] = useState(productsFromState || fallbackProducts);
  const [selectedColor, setSelectedColor] = useState(initialProductFromState?.colors?.[0] || null);
  const [selectedSize, setSelectedSize] = useState(initialProductFromState?.sizes?.[0] || null);
  const [isLoading, setIsLoading] = useState(!initialProductFromState);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialProductFromState) {
      setProduct(initialProductFromState);
      if (productsFromState) {
        setAllProducts(productsFromState);
      }
      setIsLoading(false);
      setError(null);
      return;
    }

    let isMounted = true;

    const loadProduct = async () => {
      setIsLoading(true);
      try {
        const products = await fetchProducts();
        if (!isMounted) return;
        setAllProducts(products);
        const foundProduct = findProductById(products, productId);
        if (foundProduct) {
          setProduct(foundProduct);
          setError(null);
          return;
        }
        throw new Error('Produkti nuk u gjet në server.');
      } catch (err) {
        if (!isMounted) return;
        console.error('Failed to load product', err);
        setAllProducts(fallbackProducts);
        const fallback = findProductById(fallbackProducts, productId) || fallbackProducts[0];
        if (fallback) {
          setProduct(fallback);
          setError('Produkti u ngarkua nga lista rezervë.');
        } else {
          setError('Produkti nuk u gjet.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProduct();

    return () => {
      isMounted = false;
    };
  }, [productId, initialProductFromState, productsFromState, fallbackProducts]);

  useEffect(() => {
    if (!product) return;
    if (product.colors?.length) {
      setSelectedColor((prev) => (prev && product.colors.includes(prev) ? prev : product.colors[0]));
    } else {
      setSelectedColor(null);
    }
  }, [product]);

  const availableSizes = useMemo(() => {
    if (!product) {
      return [];
    }

    if (product.variants?.length) {
      const filtered = product.variants.filter((variant) =>
        selectedColor ? variant.color === selectedColor : true
      );
      const sizes = filtered.map((variant) => variant.size).filter(Boolean);
      return Array.from(new Set(sizes));
    }

    return Array.isArray(product.sizes) ? product.sizes : [];
  }, [product, selectedColor]);

  useEffect(() => {
    if (!availableSizes.length) {
      setSelectedSize(null);
      return;
    }

    setSelectedSize((prev) => {
      if (prev && availableSizes.includes(prev)) {
        return prev;
      }
      if (availableSizes.length === 1) {
        return availableSizes[0];
      }
      return null;
    });
  }, [availableSizes]);

  const activeVariant = useMemo(() => {
    if (!product?.variants?.length) {
      return null;
    }

    const match = product.variants.find((variant) => {
      const colorMatch = selectedColor ? variant.color === selectedColor : true;
      const sizeMatch = selectedSize ? variant.size === selectedSize : true;
      return colorMatch && sizeMatch;
    });

    if (match) {
      return match;
    }

    return product.variants.find((variant) => (selectedSize ? variant.size === selectedSize : true)) || null;
  }, [product, selectedColor, selectedSize]);

  const displayPrice = activeVariant?.price ?? product?.price ?? 0;
  const hasRangeValues =
    typeof product?.minPrice === 'number' &&
    typeof product?.maxPrice === 'number' &&
    product.minPrice !== product.maxPrice;
  const showRange = !activeVariant && hasRangeValues;
  const formattedPrice = showRange
    ? `${product?.currency || '€'}${product.minPrice.toFixed(2)} - ${product?.currency || '€'}${product.maxPrice.toFixed(2)}`
    : `${product?.currency || '€'}${Number(displayPrice).toFixed(2)}`;

  const recommendedProducts = useMemo(() => {
    return allProducts
      .filter((item) => item.id !== product?.id)
      .slice(0, 4);
  }, [allProducts, product]);

  const requiresSizeSelection = availableSizes.length > 0;

  const ensureSizeSelected = () => {
    if (!requiresSizeSelection) {
      return true;
    }
    if (!selectedSize) {
      toast.error('Ju lutemi zgjidhni një madhësi para se të vazhdoni.');
      sizeSelectorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstButton = sizeSelectorRef.current?.querySelector('button');
      if (firstButton) {
        firstButton.focus({ preventScroll: true });
      }
      return false;
    }
    return true;
  };

  const handleAddToCart = () => {
    if (!product) return;
    if (!ensureSizeSelected()) {
      return;
    }

    const variant = activeVariant;
    const itemToAdd = variant
      ? {
          ...product,
          ...variant,
          id: variant.productCode || variant.id || `${product.id}-${variant.size || 'variant'}`,
          productCode: variant.productCode || variant.id || `${product.productCode}-${variant.size || 'variant'}`,
          price: variant.price ?? product.price,
          image: variant.image || product.image,
          imageUrls: product.imageUrls,
          selectedSize: variant.size || selectedSize,
          selectedColor: variant.color || selectedColor,
        }
      : {
          ...product,
          id: selectedSize ? `${product.id}-${selectedSize}` : product.id,
          productCode: selectedSize ? `${product.productCode}-${selectedSize}` : product.productCode,
          selectedSize,
          selectedColor,
        };

    addToCart(itemToAdd);
    if (selectedSize) {
      toast.success(`Produkti (${selectedSize}) u shtua në shportë.`);
    } else {
      toast.success('Produkti u shtua në shportë.');
    }
    navigate('/cart');
  };

  const handleAddToWishList = () => {
    if (!product) return;
    if (!ensureSizeSelected()) {
      return;
    }

    const wishlistPayload = activeVariant
      ? {
          ...product,
          ...activeVariant,
          id: activeVariant.productCode || activeVariant.id || `${product.id}-${activeVariant.size || 'var'}`,
          productCode: activeVariant.productCode || activeVariant.id || product.productCode,
          selectedSize: activeVariant.size || selectedSize,
          selectedColor: activeVariant.color || selectedColor,
        }
      : {
          ...product,
          selectedSize,
          selectedColor,
        };

    toggleWishlist(wishlistPayload);
    if (selectedSize) {
      const actionText = wishlistActive ? 'u hoq nga' : 'u shtua në';
      toast.success(`Madhësia ${selectedSize} ${actionText} lista e dëshirave.`);
    } else {
      toast.success('Lista e dëshirave u përditësua.');
    }
  };

  const activeProductId = product?.id || product?.productCode;
  const wishlistReferenceId = (activeVariant?.productCode || activeVariant?.id || activeProductId);
  const wishlistActive = isInWishlist(wishlistReferenceId);

  if (isLoading) {
    return (
      <>
        <NavBar />
        <SectionBreak />
        <div className="container mx-auto px-4 py-20 text-center text-gray-600">
          Duke ngarkuar detajet e produktit...
        </div>
        <SectionBreak />
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <NavBar />
        <SectionBreak />
        <div className="container mx-auto px-4 py-20 text-center text-red-600">
          Nuk u gjet asnjë produkt me këtë identifikues.
        </div>
        <SectionBreak />
        <Footer />
      </>
    );
  }

  return (
    <>
      <NavBar />
      <SectionBreak />
      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-3">
            {error}
          </div>
        )}
        <div className="flex flex-col md:flex-row gap-8">
          <div className="md:w-1/2">
            <ProductGallery images={product.imageUrls} name={product.name} />
          </div>

          <div className="md:w-1/2">
            <h1 className="text-2xl font-bold">{product.name}</h1>
            <div className="text-xl font-semibold mt-2">
              {formattedPrice}
            </div>

            {product.inStock ? (
              <div className="text-green-600 mt-1">In Stock</div>
            ) : (
              <div className="text-red-600 mt-1">Out of Stock</div>
            )}

            {product.colors?.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold mb-2">Color</h3>
                <ColorSelector
                  colors={product.colors}
                  selectedColor={selectedColor}
                  setSelectedColor={setSelectedColor}
                />
              </div>
            )}

            {availableSizes.length > 0 && (
              <div className="mt-6" ref={sizeSelectorRef}>
                <h3 className="font-semibold mb-2">Size</h3>
                <SizeSelector
                  sizes={availableSizes}
                  selectedSize={selectedSize}
                  setSelectedSize={setSelectedSize}
                />
              </div>
            )}

            <div className="mt-8">
              <p className="text-sm text-gray-500 mb-1">Tax incl., calculated at checkout (24-48 hrs)</p>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleAddToCart}
                  className="flex-1 bg-black text-white p-3 text-center"
                >
                  Shto në shportë
                </button>

                <button
                  onClick={handleAddToWishList}
                  className={`p-3 flex items-center justify-center w-12 h-12 ${wishlistActive ? 'text-red-500' : 'text-gray-900'}`}
                  aria-pressed={wishlistActive}
                  aria-label={wishlistActive ? 'Hiq nga lista e dëshirave' : 'Shto në listën e dëshirave'}
                >
                  <Heart
                    size={22}
                    fill={wishlistActive ? 'currentColor' : 'none'}
                  />
                </button>
              </div>
            </div>


            <div className="mt-8">
              <ProductDetails product={product} />
            </div>
          </div>
        </div>

        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center mb-8">RECOMMENDED PRODUCTS</h2>
          <RecommendedProducts
            products={recommendedProducts}
          />
        </div>
      </div>
      <SectionBreak />
      <Footer />
    </>
  );
};

export default Index;
